/**
 * LoRa Status Indicator
 *
 * Shows current LoRa connection status in a compact format.
 * Can be used in headers, status bars, etc.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import { clsx } from 'clsx'
import type { LoRaConnectionStatus, LoRaDevice, LoRaBackend } from '@/lib/lora'
import { LORA_EXTENDED_BOUNDARY_METERS } from '@/lib/types'
import { formatDistance } from '@/lib/geo'

interface LoRaStatusProps {
  status: LoRaConnectionStatus
  device: LoRaDevice | null
  onClick?: () => void
  showLabel?: boolean
  /** Show extended device info (name, backend, region) */
  showDeviceInfo?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export function LoRaStatus({
  status,
  device,
  onClick,
  showLabel = true,
  showDeviceInfo = false,
  size = 'md',
}: LoRaStatusProps) {
  const isConnected = status === 'connected' || status === 'ready'
  const isConnecting = status === 'connecting' || status === 'scanning' || status === 'configuring'

  const statusColor = isConnected
    ? 'bg-purple-500'
    : isConnecting
    ? 'bg-yellow-500 animate-pulse'
    : 'bg-gray-500'

  const statusText = isConnected
    ? device?.name || 'Connected'
    : isConnecting
    ? 'Connecting...'
    : 'Not connected'

  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
  }

  const textSizeClasses = {
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
  }

  // Build detailed tooltip
  const tooltipParts = [`LoRa: ${statusText}`]
  if (device) {
    if (device.backend) tooltipParts.push(`Protocol: ${device.backend}`)
    if (device.region) tooltipParts.push(`Region: ${device.region}`)
    if (device.firmwareVersion) tooltipParts.push(`FW: ${device.firmwareVersion}`)
    tooltipParts.push(`Extended range: ${formatDistance(LORA_EXTENDED_BOUNDARY_METERS)}`)
  }

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors',
        'border border-white/10 hover:border-purple-500/50',
        onClick && 'cursor-pointer hover:bg-white/5',
        !onClick && 'cursor-default'
      )}
      disabled={!onClick}
      title={tooltipParts.join('\n')}
    >
      {/* Status dot */}
      <span
        className={clsx(
          'rounded-full',
          sizeClasses[size],
          statusColor,
          isConnected && 'shadow-[0_0_6px_rgba(168,85,247,0.8)]'
        )}
      />

      {/* Label with device info */}
      {showLabel && (
        <span className={clsx('font-mono text-white/70', textSizeClasses[size])}>
          {isConnected ? (
            showDeviceInfo && device ? (
              <>📻 {device.name}</>
            ) : (
              '📻 LoRa'
            )
          ) : isConnecting ? (
            '📻...'
          ) : (
            '📻'
          )}
        </span>
      )}

      {/* Backend badge when connected */}
      {isConnected && device?.backend && (
        <LoRaBackendBadge backend={device.backend} size="xs" />
      )}

      {/* Experimental badge */}
      <span
        className={clsx(
          'px-1 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider',
          'bg-yellow-500/20 text-yellow-400'
        )}
      >
        EXP
      </span>
    </button>
  )
}

/**
 * Detailed LoRa status card for room header
 * Shows device name, protocol, region, and connection quality
 */
interface LoRaStatusCardProps {
  status: LoRaConnectionStatus
  device: LoRaDevice | null
  onClick?: () => void
}

export function LoRaStatusCard({ status, device, onClick }: LoRaStatusCardProps) {
  const isConnected = status === 'connected' || status === 'ready'
  const isConnecting = status === 'connecting' || status === 'scanning' || status === 'configuring'

  if (!isConnected && !isConnecting) {
    return null
  }

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all',
        'border bg-purple-500/10 border-purple-500/30',
        onClick && 'cursor-pointer hover:bg-purple-500/20',
        !onClick && 'cursor-default'
      )}
    >
      {/* Status indicator */}
      <span
        className={clsx(
          'w-2.5 h-2.5 rounded-full',
          isConnected
            ? 'bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.8)]'
            : 'bg-yellow-500 animate-pulse'
        )}
      />

      {/* Device info */}
      <div className="flex flex-col items-start">
        <span className="text-xs font-medium text-purple-300">
          {isConnected && device ? device.name : 'Connecting...'}
        </span>
        {isConnected && device && (
          <span className="text-[10px] text-white/50">
            {device.backend === 'meshcore' ? 'MeshCore' : 'Meshtastic'}
            {device.region && ` • ${device.region}`}
          </span>
        )}
      </div>

      {/* Extended range indicator */}
      {isConnected && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
          {formatDistance(LORA_EXTENDED_BOUNDARY_METERS)} range
        </span>
      )}
    </button>
  )
}

/**
 * Minimal LoRa status dot for tight spaces
 */
export function LoRaStatusDot({
  status,
  device,
  size = 'md',
}: {
  status: LoRaConnectionStatus
  device?: LoRaDevice | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const isConnected = status === 'connected' || status === 'ready'
  const isConnecting = status === 'connecting' || status === 'scanning' || status === 'configuring'

  const statusColor = isConnected
    ? 'bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.8)]'
    : isConnecting
    ? 'bg-yellow-500 animate-pulse'
    : 'bg-gray-500'

  const sizeClasses = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  }

  // Build tooltip with device info
  let tooltip = `LoRa: ${isConnected ? 'Connected' : isConnecting ? 'Connecting' : 'Disconnected'}`
  if (isConnected && device) {
    tooltip = `LoRa: ${device.name} (${device.backend})`
    if (device.region) tooltip += ` • ${device.region}`
  }

  return (
    <span
      className={clsx('rounded-full', sizeClasses[size], statusColor)}
      title={tooltip}
    />
  )
}

/**
 * Backend badge component
 */
export function LoRaBackendBadge({
  backend,
  size = 'sm',
}: {
  backend: LoRaBackend
  size?: 'xs' | 'sm' | 'md'
}) {
  const sizeClasses = {
    xs: 'px-1 py-0.5 text-[8px]',
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-1 text-xs',
  }

  return (
    <span
      className={clsx(
        'rounded font-mono uppercase tracking-wider',
        sizeClasses[size],
        backend === 'meshtastic'
          ? 'bg-blue-500/20 text-blue-400'
          : 'bg-green-500/20 text-green-400'
      )}
    >
      {backend === 'meshtastic' ? 'Mesh' : 'Core'}
    </span>
  )
}
